/**
 * DockingManager - Layout Management System
 * 
 * Manages the docking layout state, persistence, and default configurations.
 * Handles saving/loading from LocalStorage with versioning and migration.
 */

import { logger } from '../core/Logger';

export interface DockingLayout {
  version: 2;
  regions: {
    left: DockRegionConfig;
    right: DockRegionConfig;
    top: DockRegionConfig;
    bottom: DockRegionConfig;
  };
  detachedWindows: DetachedWindowConfig[];
}

export interface DockRegionConfig {
  size: number; // pixels
  visible: boolean;
  tabGroups: TabGroupConfig[];
}

export interface TabGroupConfig {
  tabs: string[]; // panel IDs
  activeTab: string; // panel ID
}

export interface DetachedWindowConfig {
  panelId: string;
  bounds: { x: number; y: number; width: number; height: number };
}

export interface PanelDescriptor {
  id: string;
  title: string;
  icon?: string;
  defaultRegion: 'left' | 'right' | 'top' | 'bottom';
  defaultSize?: { width?: number; height?: number };
  detachable: boolean;
}

const STORAGE_KEY = 'himum.docking.v2';
const OLD_STORAGE_KEY = 'himum.debugHud.v1';

const DEFAULT_LAYOUT: DockingLayout = {
  version: 2,
  regions: {
    left: {
      size: 400,
      visible: true,
      tabGroups: [
        {
          tabs: ['debug', 'post', 'lighting', 'environment', 'behaviors'],
          activeTab: 'debug',
        },
      ],
    },
    right: {
      size: 560,
      visible: true,
      tabGroups: [
        {
          tabs: ['scene'],
          activeTab: 'scene',
        },
      ],
    },
    bottom: {
      size: 250,
      visible: true,
      tabGroups: [
        {
          tabs: ['console', 'timeline', 'performance'],
          activeTab: 'console',
        },
      ],
    },
    top: {
      size: 80,
      visible: true,
      tabGroups: [
        {
          tabs: ['map'],
          activeTab: 'map',
        },
      ],
    },
  },
  detachedWindows: [],
};

export class DockingManager {
  private panels = new Map<string, PanelDescriptor>();
  private layout: DockingLayout;

  constructor() {
    this.layout = this.loadLayout();
  }

  registerPanel(descriptor: PanelDescriptor): void {
    this.panels.set(descriptor.id, descriptor);
  }

  getLayout(): DockingLayout {
    return this.layout;
  }

  updateRegionSize(regionId: keyof DockingLayout['regions'], size: number): void {
    this.layout.regions[regionId].size = size;
    this.saveLayout();
  }

  updateActiveTab(regionId: keyof DockingLayout['regions'], groupIndex: number, tabId: string): void {
    const region = this.layout.regions[regionId];
    if (region.tabGroups[groupIndex]) {
      region.tabGroups[groupIndex].activeTab = tabId;
      this.saveLayout();
    }
  }

  toggleRegionVisibility(regionId: keyof DockingLayout['regions']): void {
    this.layout.regions[regionId].visible = !this.layout.regions[regionId].visible;
    this.saveLayout();
  }

  setRegionVisibility(regionId: keyof DockingLayout['regions'], visible: boolean): void {
    const region = this.layout.regions[regionId];

    // If a user drags a resizer to (near) zero, the region can end up "visible" but effectively not shown.
    // Any reveal action should restore a sane size so the panel actually appears.
    const minSize = 48;
    const ensureSaneSize = () => {
      if (region.size < minSize) {
        region.size = DEFAULT_LAYOUT.regions[regionId].size;
        return true;
      }
      return false;
    };

    let changed = false;
    if (region.visible !== visible) {
      region.visible = visible;
      changed = true;
    }
    if (visible) {
      changed = ensureSaneSize() || changed;
    }

    if (changed) {
      this.saveLayout();
    }
  }

  removePanel(regionId: keyof DockingLayout['regions'], groupIndex: number, panelId: string): void {
    const region = this.layout.regions[regionId];
    if (!region.tabGroups[groupIndex]) {
      logger.ui.warn(`Invalid group index for removePanel: region=${regionId}, group=${groupIndex}`);
      return;
    }

    const group = region.tabGroups[groupIndex];
    const panelIndex = group.tabs.indexOf(panelId);
    
    if (panelIndex === -1) {
      logger.ui.warn(`Panel ${panelId} not found in region ${regionId}, group ${groupIndex}`);
      return;
    }

    // Remove the panel
    group.tabs.splice(panelIndex, 1);

    // If the removed panel was active, switch to another tab
    if (group.activeTab === panelId) {
      if (group.tabs.length > 0) {
        // Activate the tab at the same index, or the last tab if index is out of bounds
        group.activeTab = group.tabs[Math.min(panelIndex, group.tabs.length - 1)];
      } else {
        group.activeTab = '';
      }
    }

    this.saveLayout();
  }

  addFloatingPanel(panelId: string, bounds: { x: number; y: number; width: number; height: number }): void {
    // Check if panel is already floating
    const existingIndex = this.layout.detachedWindows.findIndex(w => w.panelId === panelId);
    if (existingIndex !== -1) {
      // Update bounds of existing floating panel
      this.layout.detachedWindows[existingIndex].bounds = bounds;
    } else {
      // Add new floating panel
      this.layout.detachedWindows.push({
        panelId,
        bounds
      });
    }
    this.saveLayout();
  }

  removeFloatingPanel(panelId: string): void {
    const index = this.layout.detachedWindows.findIndex(w => w.panelId === panelId);
    if (index !== -1) {
      this.layout.detachedWindows.splice(index, 1);
      this.saveLayout();
    }
  }

  updateFloatingPanelBounds(panelId: string, updates: Partial<{ x: number; y: number; width: number; height: number }>): void {
    const window = this.layout.detachedWindows.find(w => w.panelId === panelId);
    if (window) {
      window.bounds = { ...window.bounds, ...updates };
      this.saveLayout();
    }
  }

  dockFloatingPanel(panelId: string, targetRegion: keyof DockingLayout['regions'], targetGroupIndex = 0): void {
    // Remove from floating windows.
    this.layout.detachedWindows = this.layout.detachedWindows.filter(w => w.panelId !== panelId);

    const region = this.layout.regions[targetRegion];
    if (!region.tabGroups[targetGroupIndex]) {
      region.tabGroups[targetGroupIndex] = { tabs: [], activeTab: '' };
    }

    const group = region.tabGroups[targetGroupIndex];
    if (!group.tabs.includes(panelId)) {
      group.tabs.push(panelId);
    }
    group.activeTab = panelId;

    this.saveLayout();
  }

  /**
   * Move a panel from one region to another
   * @param panelId - ID of the panel to move
   * @param fromRegion - Source region
   * @param toRegion - Target region
   * @param fromGroupIndex - Index of tab group in source region (default 0)
   * @param toGroupIndex - Index of tab group in target region (default 0)
   */
  movePanel(
    panelId: string,
    fromRegion: keyof DockingLayout['regions'],
    toRegion: keyof DockingLayout['regions'],
    fromGroupIndex = 0,
    toGroupIndex = 0
  ): boolean {
    const sourceRegion = this.layout.regions[fromRegion];
    const targetRegion = this.layout.regions[toRegion];

    if (!sourceRegion.tabGroups[fromGroupIndex] || !targetRegion.tabGroups[toGroupIndex]) {
      logger.ui.warn(`Invalid group index for movePanel: from=${fromGroupIndex}, to=${toGroupIndex}`);
      return false;
    }

    const sourceGroup = sourceRegion.tabGroups[fromGroupIndex];
    const targetGroup = targetRegion.tabGroups[toGroupIndex];

    // Find panel in source group
    const panelIndex = sourceGroup.tabs.indexOf(panelId);
    if (panelIndex === -1) {
      logger.ui.warn(`Panel ${panelId} not found in source region ${fromRegion}`);
      return false;
    }

    // Remove from source
    sourceGroup.tabs.splice(panelIndex, 1);
    
    // If removed panel was active, set new active tab
    if (sourceGroup.activeTab === panelId) {
      sourceGroup.activeTab = sourceGroup.tabs[0] || '';
    }

    // Add to target
    if (!targetGroup.tabs.includes(panelId)) {
      targetGroup.tabs.push(panelId);
      // Make it active in target
      targetGroup.activeTab = panelId;
    }

    this.saveLayout();
    return true;
  }

  saveLayout(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.layout));
    } catch (error) {
      logger.ui.warn('Failed to save docking layout:', error);
    }
  }

  loadLayout(): DockingLayout {
    try {
      // Try loading v2 layout
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as DockingLayout;
        if (parsed.version === 2) {
          return this.mergeWithDefaults(parsed);
        }
      }

      // Try migrating from v1
      const oldRaw = localStorage.getItem(OLD_STORAGE_KEY);
      if (oldRaw) {
        const migrated = this.migrateFromV1(oldRaw);
        if (migrated) {
          this.saveLayout();
          return migrated;
        }
      }
    } catch (error) {
      logger.ui.warn('Failed to load docking layout, using defaults:', error);
    }

    return structuredClone(DEFAULT_LAYOUT);
  }

  resetLayout(): void {
    this.layout = structuredClone(DEFAULT_LAYOUT);
    this.saveLayout();
  }

  private mergeWithDefaults(layout: DockingLayout): DockingLayout {
    // Ensure all regions exist with defaults
    const merged: DockingLayout = {
      version: 2,
      regions: {
        left: layout.regions.left || DEFAULT_LAYOUT.regions.left,
        right: layout.regions.right || DEFAULT_LAYOUT.regions.right,
        top: layout.regions.top || DEFAULT_LAYOUT.regions.top,
        bottom: layout.regions.bottom || DEFAULT_LAYOUT.regions.bottom,
      },
      detachedWindows: layout.detachedWindows || [],
    };

    // Add new panels that don't exist in saved layout
    // Check left region for environment panel
    if (merged.regions.left.tabGroups[0]) {
      const leftTabs = merged.regions.left.tabGroups[0].tabs;
      if (!leftTabs.includes('environment')) {
        leftTabs.push('environment');
      }
    }

    // Ensure top region has map panel
    if (!merged.regions.top.tabGroups[0]) {
      merged.regions.top.tabGroups[0] = { tabs: ['map'], activeTab: 'map' };
    } else if (!merged.regions.top.tabGroups[0].tabs.includes('map')) {
      merged.regions.top.tabGroups[0].tabs.push('map');
      merged.regions.top.tabGroups[0].activeTab = 'map';
    }
    
    // Ensure top region is visible and has proper size
    if (!merged.regions.top.visible || merged.regions.top.size === 0) {
      merged.regions.top.visible = true;
      merged.regions.top.size = 80;
    }

    return merged;
  }

  private migrateFromV1(oldData: string): DockingLayout | null {
    try {
      const old = JSON.parse(oldData);
      
      // Extract panel visibility and active tabs from old format
      const panels = old.panels || {};
      
      const layout: DockingLayout = structuredClone(DEFAULT_LAYOUT);
      
      // Map old panel states to new layout
      // Note: old format had simpler panel toggles, we preserve what we can
      if (panels.sidebar !== undefined) {
        layout.regions.left.visible = panels.sidebar;
      }
      if (panels.scene !== undefined) {
        layout.regions.right.visible = panels.scene;
      }
      if (panels.console !== undefined) {
        layout.regions.bottom.visible = panels.console;
      }

      return layout;
    } catch {
      return null;
    }
  }
}
